import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCcw, MapPin, CalendarClock, Camera } from "lucide-react";
import { toast } from "sonner";
import { Spinner, StatusBadge } from "../../components/common";
import { FloorAssessment } from "../../components/FloorAssessment";
import { GpsPhotoGallery } from "../../components/GpsPhotoGallery";
import { GpsPhotoCapture, uploadPropertyPhoto, heldToQueuePhotos } from "../../components/GpsPhotoCapture";
import { VisitPhotosStep, visitPhotoCount } from "../../components/VisitPhotosStep";
import { CollectPaymentFlow, ImportBlockNotice, useImportCheck } from "../../components/CollectPaymentFlow";
import { MobileVerifyCard, MobileVerifyDrawer, useMobileVerification } from "../../components/MobileVerifyGate";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../../components/ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { gpsPhotosToday, VISIT_PHOTO_REQUIRED_MESSAGE } from "../../lib/gps";
import { getPack, queueAdd, useOnline, isNetworkError } from "../../lib/offline";
import { WifiOff, History } from "lucide-react";
import { MANUAL_VISIT_OUTCOMES, SameDayVisitBanner, SupervisorVisitBanner, VisitTimeline, useVisitHistory, VISIT_OUTCOME_META } from "../../components/VisitTimeline";
import { CollectionSummaryCards, PaymentHistoryTable, ResponsibleStaff } from "../../components/PropertyCollectionSummary";
import { useRoleModules } from "../../lib/roleModules";
import { ReadOnlyNotice } from "../../components/FeatureGate";

const VISIT_OUTCOMES = MANUAL_VISIT_OUTCOMES;

/** Offline fallback (cached pack has demands but no payments): best-effort totals from the demand lines. */
const summaryFromDemands = (demands) => {
  const total = demands.reduce((s, d) => s + Number(d.total_demand || 0), 0);
  const paid = demands.reduce((s, d) => s + Number(d.paid_amount || 0), 0);
  const balance = demands.reduce((s, d) => s + Number(d.balance_amount || 0), 0);
  return { total_demand: total, total_collected: paid, allocated: paid, balance_due: balance, advance_credit: 0,
    payments_count: 0, last_payment_date: null, demands_count: demands.length, has_demand: demands.length > 0,
    payment_status: total > 0 && balance <= 0 ? "paid" : paid > 0 ? "partial" : "pending" };
};

export default function CollectorProperty() {
  const { code, propId } = useParams();
  // Phase 152: collector sub-features — collect_payment / visit_actions (visit log, mark visited) / field.reassessments
  const access = useRoleModules(code);
  const canCollect = access.canWrite("assigned", "collect_payment");
  const canVisit = access.canWrite("assigned", "visit_actions");
  const canReassess = access.featureAllowed("field", "reassessments");
  const navigate = useNavigate();
  const { auth } = useAuth();
  const online = useOnline();
  const [detail, setDetail] = useState(null);
  const [offlineView, setOfflineView] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [visit, setVisit] = useState({ outcome: "", follow_up_date: "", notes: "" });
  const [visitBusy, setVisitBusy] = useState(false);
  const [heldPhotos, setHeldPhotos] = useState([]);       // Phase 132: GPS photos captured inside the visit drawer
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletePhoto, setDeletePhoto] = useState(null);
  const { history, reload: reloadVisits } = useVisitHistory(code, propId);

  const load = useCallback(() => api.get(`/t/${code}/properties/${propId}`)
    .then((r) => { setDetail(r.data); setOfflineView(false); })
    .catch(async (err) => {
      // offline fallback: assigned-property detail from the cached pack
      const pack = await getPack(code).catch(() => null);
      const p = (pack?.assigned_properties || []).find((x) => x.id === propId);
      if (p) {
        setDetail({ property: p, demands: pack?.demands_by_property?.[propId] || [], payments: [], custom_fields: [] });
        setOfflineView(true);
      } else if (err?.response) {
        throw err;
      }
    }), [code, propId]);
  useEffect(() => {
    load().catch(() => {});
  }, [load, code]);
  // Phase 149: a payment recorded for this property on another device / by the office refreshes the ledger here
  useLiveCollections(code, () => { load().catch(() => {}); });
  // Phase 177: bulk-imported payment on this PIN → manual entry blocked (full) or capped to the remaining balance
  const importCheck = useImportCheck(code, propId, detail);

  // Phase 134: the owner's mobile number must be verified (once per day, by me) before a visit / collection
  const mv = useMobileVerification({ code, property: detail?.property, verification: detail?.mobile_verification,
    userId: auth?.user?.id, online, onChanged: () => { if (online) load().catch(() => {}); } });

  if (!detail) return <Spinner />;
  const { property, demands, payments } = detail;
  const collectionSummary = detail.collection_summary || summaryFromDemands(demands || []);
  const allPhotos = [...(property.third_party_photos || []), ...(property.property_photos || [])];

  // Phase 132: standalone "Add GPS photo" — the capture component only hands over photos WITH a real GPS fix
  const onStandalonePhoto = async (photo) => {
    setUploadingPhoto(true);
    try {
      await uploadPropertyPhoto(code, propId, photo);
      toast.success(`Photo saved with GPS (${photo.lat.toFixed(5)}, ${photo.lng.toFixed(5)})`);
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setUploadingPhoto(false); }
  };

  const confirmDeletePhoto = async () => {
    try {
      await api.delete(`/t/${code}/field/property-photos/${propId}/${deletePhoto.file_id}`);
      toast.success("Photo removed");
      setDeletePhoto(null);
      await load();
    } catch (err) { toast.error(errMsg(err)); setDeletePhoto(null); }
  };

  const pendingDemands = demands.filter((d) => d.balance_amount > 0);
  const totalDue = pendingDemands.reduce((s, d) => s + d.balance_amount, 0);

  const doMarkVisited = async () => {
    if (!property.assignment?.id) return;
    const offlineSave = async () => {
      await queueAdd(code, "mark_visited", { assignment_id: property.assignment.id },
        { label: `Marked visited • ${property.pin}` });
      toast.success("Saved on device — syncs when back online");
    };
    if (!online) { await offlineSave(); return; }
    try {
      await api.put(`/t/${code}/field/mark-visited`, { assignment_id: property.assignment.id });
      toast.success("Marked as visited");
      load();
    } catch (err) {
      if (isNetworkError(err)) await offlineSave();
      else toast.error(errMsg(err));
    }
  };

  // Phase 134: mobile verification first (opens the step and continues automatically once done)
  const markVisited = () => mv.gate(doMarkVisited);

  const closeVisitDrawer = () => {
    setVisitOpen(false);
    setVisit({ outcome: "", follow_up_date: "", notes: "" });
    setHeldPhotos([]);
  };

  const submitVisit = async () => {
    const cfg = VISIT_OUTCOMES.find((o) => o.value === visit.outcome);
    if (!cfg) return toast.error("Select the visit outcome");
    if (cfg.needsDate && !visit.follow_up_date) return toast.error("Select the date");
    if (cfg.needsReason && !visit.notes.trim()) return toast.error("Enter the reason for no payment");
    // Phase 132: at least one GPS photo (captured now, or earlier today by me) is mandatory
    if (visitPhotoCount(heldPhotos, property, auth?.user?.id) === 0) return toast.error(VISIT_PHOTO_REQUIRED_MESSAGE);
    setVisitBusy(true);
    const todayIds = gpsPhotosToday(property.property_photos, auth?.user?.id).map((p) => p.file_id);
    // Phase 184: the visit instant is the device clock at the moment of saving — sent online and offline alike,
    // so reports show when the collector actually logged the visit (not when uploads/sync finished)
    const visitedAt = new Date().toISOString();
    const payload = {
      property_id: property.id, outcome: visit.outcome,
      follow_up_date: visit.follow_up_date || null, notes: visit.notes || null,
      photo_file_ids: todayIds, visited_at: visitedAt,
    };
    const offlineSave = async () => {
      // photos ride along (data URL + GPS); the sync engine uploads them with the same client_ref (no duplicates)
      await queueAdd(code, "visit", { ...payload },
        { photos: heldToQueuePhotos(heldPhotos), label: `Visit • ${property.pin} (${cfg.label})` });
      toast.success("Visit saved on device — the owner is notified when it syncs");
      closeVisitDrawer();
    };
    try {
      if (!online) { await offlineSave(); return; }
      try {
        const uploaded = [];
        for (const photo of heldPhotos) {
          uploaded.push((await uploadPropertyPhoto(code, property.id, photo)).file_id);
        }
        const saved = await api.post(`/t/${code}/field/visit-log`, { ...payload, photo_file_ids: [...todayIds, ...uploaded] });
        // Phase 217: a second visitor's visit-only log is stored but not counted in their name
        if (saved.data?.same_day_notice) toast.warning(saved.data.same_day_notice, { duration: 8000 });
        else toast.success("Visit recorded — owner will be notified" + (visit.follow_up_date ? ` • follow-up on ${visit.follow_up_date}` : ""));
        closeVisitDrawer();
        load();
        reloadVisits();
      } catch (err) {
        if (isNetworkError(err)) { await offlineSave(); return; }
        toast.error(errMsg(err));
      }
    } finally {
      setVisitBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="collector-back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {(!online || offlineView) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-amber-900" data-testid="property-offline-banner">
          <WifiOff className="h-4 w-4 shrink-0" />
          <p className="text-xs">Offline — showing saved data. Visits and reassessments are stored on the device and sync automatically. Payment collection needs internet.</p>
        </div>
      )}

      <SupervisorVisitBanner summary={history?.summary} />
      <SameDayVisitBanner sameDay={history?.same_day} />

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono font-semibold">{property.pin}</p>
              <p className="font-medium">{property.owner_name}</p>
              <p className="text-xs text-muted-foreground">S/o {property.father_spouse_name || "-"}</p>
            </div>
            <StatusBadge status={property.payment_status} />
          </div>
          <div className="mt-3 space-y-1 text-sm">
            <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {property.address || "-"}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div className="bg-muted rounded-lg py-2"><p className="text-xs text-muted-foreground">Zone</p><p className="text-sm font-medium">{property.zone_label || "-"}</p></div>
            <div className="bg-muted rounded-lg py-2"><p className="text-xs text-muted-foreground">Ward</p><p className="text-sm font-medium">{property.ward_label || "-"}</p></div>
            <div className="bg-muted rounded-lg py-2"><p className="text-xs text-muted-foreground">Mohalla</p><p className="text-sm font-medium">{property.mohalla_label || "-"}</p></div>
          </div>
          <div className="bg-muted rounded-lg py-2 mt-2 text-center" data-testid="collector-road-width">
            <p className="text-xs text-muted-foreground">Road Width</p>
            <p className="text-sm font-medium">{property.road_width_label || "-"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Phase 134: verify (confirm / update) the owner's mobile number — gates Record Visit, Mark visited, Collect Payment */}
      <MobileVerifyCard mv={mv} testId="collector-mobile-verify" />

      <FloorAssessment floors={property.assessment_floors} />

      {/* Phase 104: what was raised / collected / still due + who is accountable */}
      <Card className="shadow-sm" data-testid="collector-collection-card">
        <CardContent className="p-4 space-y-3">
          <p className="font-medium text-sm">Collection summary</p>
          <CollectionSummaryCards summary={collectionSummary} testId="collector-collection-summary" />
          {detail.responsible && <ResponsibleStaff code={code} responsible={detail.responsible} testId="collector-responsible" />}
        </CardContent>
      </Card>

      {online && !offlineView && (
        <Card className="shadow-sm">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-sm flex items-center gap-1.5">
                <Camera className="h-4 w-4" /> Property photos
              </p>
              <GpsPhotoCapture testId="collector-add-photo" busy={uploadingPhoto} onCapture={onStandalonePhoto} />
            </div>
            {allPhotos.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="collector-photos-empty">
                No photos yet — capture the site during your visit. Every photo is stamped with your GPS location (location must be on).
              </p>
            ) : (
              <GpsPhotoGallery code={code} photos={allPhotos} title="GPS-tagged photos"
                canDelete={(p) => p.uploaded_by === auth?.user?.id}
                onDelete={(p) => setDeletePhoto(p)} />
            )}
          </CardContent>
        </Card>
      )}

      {/* delete own photo confirm */}
      <AlertDialog open={!!deletePhoto} onOpenChange={(v) => !v && setDeletePhoto(null)}>
        <AlertDialogContent data-testid="collector-photo-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              The photo will be removed from this property. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="collector-photo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePhoto}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="collector-photo-delete-confirm-button">Remove photo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-medium text-sm">Demand breakdown</p>
            <p className="font-semibold tabular-nums text-destructive">{fmtINR(totalDue)} due</p>
          </div>
          <div className="space-y-2">
            {demands.map((d) => (
              <div key={d.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-medium">{d.tax_head_name}</p>
                  {d.tp_breakdown?.components?.length > 0 ? (
                    <div className="space-y-0.5" data-testid={`demand-breakdown-${d.id}`}>
                      <p className="text-[11px] text-muted-foreground">{d.fy_label}</p>
                      {d.tp_breakdown.components.map((c) => (
                        <p key={c.key || c.label} className="text-[11px] text-muted-foreground">{c.label}: <span className="tabular-nums">{fmtINR(c.amount)}</span></p>
                      ))}
                      {Math.abs(d.tp_breakdown.adjustment || 0) >= 0.01 && (
                        <p className="text-[11px] text-muted-foreground italic" data-testid={`demand-breakdown-adjustment-${d.id}`}>Adjustment: <span className="tabular-nums">{fmtINR(d.tp_breakdown.adjustment)}</span></p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">{d.fy_label} • Arrear {fmtINR(d.arrear_amount)} + Current {fmtINR(d.current_amount)}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold tabular-nums">{fmtINR(d.balance_amount)}</p>
                  <StatusBadge status={d.payment_status} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {canCollect && <ImportBlockNotice check={importCheck.check} testId="collector-import-check" />}
      {(canCollect || canReassess) && (
      <div className={`grid ${canCollect && canReassess ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
        {canCollect && <CollectPaymentFlow code={code} property={property} pendingDemands={pendingDemands} disabled={totalDue <= 0 || !online} gate={mv.gate} importCheck={importCheck.check} />}
        {canReassess && <Button size="lg" variant="outline" className="h-12" asChild data-testid="collector-reassess-button">
          <Link to={`/${code}/collector/reassessment/${property.id}`}><RefreshCcw className="h-4 w-4 mr-1" /> Reassessment</Link>
        </Button>}
      </div>
      )}
      {canVisit && <Button size="lg" variant="secondary" className="w-full h-11" onClick={() => mv.gate(() => setVisitOpen(true))} data-testid="record-visit-button">
        <CalendarClock className="h-4 w-4 mr-1.5" /> Record Visit / Next Date
      </Button>}
      {!canCollect && !canVisit && <ReadOnlyNotice what="this property" testId="collector-property-readonly-notice" />}
      {(property.next_visit_date || property.last_visit_outcome) && (
        <p className="text-xs text-muted-foreground text-center" data-testid="last-visit-info">
          {property.next_visit_date ? `Next visit / collection date: ${property.next_visit_date}` : ""}
          {property.last_visit_outcome ? ` • Last visit: ${VISIT_OUTCOME_META[property.last_visit_outcome]?.label || property.last_visit_outcome}` : ""}
        </p>
      )}
      {canVisit && property.assignment && !["visited", "collected", "partially_collected"].includes(property.assignment.status) && (
        <Button variant="secondary" className="w-full" onClick={markVisited} data-testid="collector-mark-visited-button">Mark as visited</Button>
      )}

      {!offlineView && (
        <Card className="shadow-sm" data-testid="collector-collection-history-card">
          <CardContent className="p-4">
            <p className="font-medium text-sm mb-2">Collection history{payments.length ? ` (${payments.length})` : ""}</p>
            <PaymentHistoryTable code={code} payments={payments} testId="collector-collection-history" />
          </CardContent>
        </Card>
      )}

      {history && history.visits.length > 0 && (
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="font-medium text-sm mb-3 flex items-center gap-1.5" data-testid="collector-visit-history-title">
              <History className="h-4 w-4" /> Visit history ({history.summary.total_visits})
            </p>
            <VisitTimeline code={code} visits={history.visits} />
          </CardContent>
        </Card>
      )}

      <MobileVerifyDrawer mv={mv} testId="collector-mobile-verify" />

      <Drawer open={visitOpen} onOpenChange={(v) => (v ? setVisitOpen(true) : closeVisitDrawer())}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">Record visit outcome</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto">
            <div className="space-y-1">
              <Label>What happened at the property? *</Label>
              <Select value={visit.outcome} onValueChange={(v) => v && setVisit((s) => ({ ...s, outcome: v }))}>
                <SelectTrigger className="h-11" data-testid="visit-outcome-select">
                  <SelectValue placeholder="Select outcome">{(VISIT_OUTCOMES.find((o) => o.value === visit.outcome) || {}).label || null}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {VISIT_OUTCOMES.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`visit-outcome-option-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>
                {visit.outcome === "preferred_date" ? "Owner's preferred collection date *"
                  : visit.outcome === "next_visit" ? "Next visit date *" : "Follow-up date (optional)"}
              </Label>
              <Input type="date" className="h-11" value={visit.follow_up_date}
                onChange={(e) => setVisit((s) => ({ ...s, follow_up_date: e.target.value }))} data-testid="visit-date-input" />
            </div>
            <div className="space-y-1">
              <Label>{visit.outcome === "no_payment" ? "Reason for no payment *" : "Notes"}</Label>
              <Textarea rows={2} value={visit.notes} onChange={(e) => setVisit((s) => ({ ...s, notes: e.target.value }))}
                placeholder={visit.outcome === "no_payment" ? "e.g. Owner said salary not credited yet" : "e.g. Owner asked to come after salary day"} data-testid="visit-notes-input" />
            </div>
            {/* Phase 132: at least one GPS photo is mandatory to record the visit */}
            <VisitPhotosStep code={code} property={property} userId={auth?.user?.id} held={heldPhotos} setHeld={setHeldPhotos} testId="visit-photos" />
            <p className="text-[11px] text-muted-foreground">
              The owner is notified by SMS / WhatsApp / Email, and this follow-up appears in your To-Do list.
            </p>
            <Button size="lg" className="w-full h-12" onClick={submitVisit}
              disabled={visitBusy || visitPhotoCount(heldPhotos, property, auth?.user?.id) === 0} data-testid="visit-submit-button">
              {visitBusy ? "Saving..." : "Save visit & notify owner"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
